import { validate } from '@cfworker/json-schema';
import {
  defaultLocale,
  loc_activities,
  loc_activity,
  loc_activityReportDescription,
  loc_organizationMembers,
  loc_people,
  loc_person,
  loc_personReportDescription,
  loc_projectOrganization,
  loc_projectOrganizationReportDescription,
  loc_projectOrganizations,
  loc_userOrganizationReportDescription,
  Locale
} from '@cumu/strings';
import { minDate } from '../date';
import { ExpressionReturnType } from '../expressions/common';
import { reportColumnIndexToIdentifier } from '../expressions/reports';
import {
  issueOpenedIcon,
  organizationIcon,
  peopleIcon,
  personRolodexIcon
} from '../icons';
import {
  integer_lookup_item_schema,
  uuid_lookup_item_schema
} from '../schemas';
import { activityStatusTitles, openActivityStatuses } from './activity';
import type { ActivityType } from './activity';
import { AddressFields } from './address';
import { DashboardElement } from './dashboard';
import {
  EntityTypeName,
  Property,
  PropertyType,
  wellKnownProperties
} from './entity-type';
import { LookupItem } from './lookup-item';
import { hasRoleLevel, UserOrganizationListItem } from './organization';
import { OrganizationSharedResource, SharedResource } from './resource';

export const reportPageSize = 5000;

export type ReportEntityType =
  'activity' | 'person' | 'user_organization' | 'organization_project';

export const reportEntityTypes: ReportEntityType[] = [
  'activity',
  'person',
  'user_organization',
  'organization_project'
];

export const reportEntityInfo: Record<
  ReportEntityType,
  {
    singular: Record<Locale, string>;
    plural: Record<Locale, string>;
    description: Record<Locale, string>;
    included: EntityTypeName[];
    icon: string;
    subqueries: {
      value:
        | 'root-activity'
        | 'root-descendant-activities'
        | 'grandparent-activity'
        | 'parent-activity'
        | 'child-activities'
        | 'grandchild-activities'
        | 'referenced-activities'
        | 'second-degree-referenced-activities'
        | 'referenced-root-descendant-activities'
        | 'referencing-activities'
        | 'person-activities'
        | 'sibling-activities'
        | 'assigned-activities'
        | 'created-activities'
        | 'contracted-activities'
        | 'organization-project-users';
      text: string;
      group?: string;
    }[];
  }
> = {
  activity: {
    singular: loc_activity,
    plural: loc_activities,
    description: loc_activityReportDescription,
    included: ['person'],
    icon: issueOpenedIcon,
    subqueries: [
      { value: 'root-activity', text: 'Root activity' },
      {
        value: 'root-descendant-activities',
        text: 'Root activity & descendants'
      },
      { value: 'parent-activity', text: 'Parent activity' },
      { value: 'child-activities', text: 'Child activities' },
      { value: 'person-activities', text: `Person's activities` },
      { value: 'sibling-activities', text: 'Sibling activities' },
      { value: 'referenced-activities', text: 'Referenced activities' },
      {
        value: 'second-degree-referenced-activities',
        text: 'Second-degree referenced activities'
      },
      {
        value: 'referenced-root-descendant-activities',
        text: 'Referenced root activities & descendants'
      },
      { value: 'referencing-activities', text: 'Referencing activities' },
      { value: 'grandparent-activity', text: 'Grandparent activity' },
      {
        value: 'grandchild-activities',
        text: 'Grandchild activities (deprecated)'
      }
    ]
  },
  organization_project: {
    singular: loc_projectOrganization,
    plural: loc_projectOrganizations,
    description: loc_projectOrganizationReportDescription,
    included: [],
    icon: organizationIcon,
    subqueries: [
      { value: 'assigned-activities', text: 'Assigned activities' },
      { value: 'contracted-activities', text: 'Contracted activities' },
      { value: 'created-activities', text: 'Created activities' },
      {
        value: 'organization-project-users',
        text: 'Organization project users'
      }
    ]
  },
  person: {
    singular: loc_person,
    plural: loc_people,
    description: loc_personReportDescription,
    included: [],
    icon: peopleIcon,
    subqueries: [{ value: 'child-activities', text: 'Activities' }]
  },
  user_organization: {
    singular: loc_organizationMembers,
    plural: loc_organizationMembers,
    description: loc_userOrganizationReportDescription,
    included: [],
    icon: personRolodexIcon,
    subqueries: [
      { value: 'assigned-activities', text: 'Assigned activities' },
      { value: 'created-activities', text: 'Created activities' }
    ]
  }
};

export enum ReportSharedWith {
  None = 0,
  Organization = 1,
  Project = 2,
  ProjectContractedOrganizations = 3,
  ProjectDirectlyContractedOrganizations = 4,
  SpecificOrganizations = 5,
  SpecificUsers = 6
}

export type ReportLayout = 'table' | 'dashboard';

export const reportLayouts: ReportLayout[] = ['table', 'dashboard'];

export type ReportSortDirection = 'asc' | 'desc';

export type ReportFilterComparisonOperator =
  '=' | '<>' | '>' | '>=' | '<' | '<=';

export type ReportFilterOperator =
  | ReportFilterComparisonOperator
  | 'in'
  | 'not in'
  | 'startswith'
  | 'notstartswith'
  | 'contains'
  | 'notcontains'
  | 'blank'
  | 'notblank';

export const operatorNames: Record<ReportFilterOperator, string> = {
  '=': 'Equal to',
  '<>': 'Not equal to',
  '<': 'Less than',
  '<=': 'Less than or equal to',
  '>': 'Greater than',
  '>=': 'Greater than or equal to',
  in: 'Any of',
  'not in': 'None of',
  startswith: 'Starts with',
  notstartswith: 'Does not start with',
  contains: 'Contains',
  notcontains: 'Does not contain',
  blank: 'Is blank/unanswered',
  notblank: 'Is not blank/unanswered'
};

export const dateOperatorNames: Record<ReportFilterOperator, string> =
  Object.assign({}, operatorNames, {
    '<': 'Before',
    '<=': 'On or before',
    '>': 'After',
    '>=': 'On or after'
  });

export const operators = /*@__PURE__*/ Object.keys(
  operatorNames
) as ReportFilterOperator[];

export const comparisonOperators: ReportFilterComparisonOperator[] = [
  '=',
  '<>',
  '>',
  '>=',
  '<',
  '<='
];

export function isComparisonOperator(
  op: string
): op is ReportFilterComparisonOperator {
  return comparisonOperators.includes(op as ReportFilterComparisonOperator);
}

export function compare(
  op: ReportFilterComparisonOperator,
  a: number | string,
  b: number | string
) {
  if (typeof a !== typeof b) {
    throw new Error(
      `Expected typeof a "${typeof a}" to match typeof "${typeof b}".`
    );
  }
  switch (op) {
    case '<':
      return a < b;
    case '<=':
      return a <= b;
    case '<>':
      return a !== b;
    case '=':
      return a === b;
    case '>':
      return a > b;
    case '>=':
      return a >= b;
    default:
      throw new Error(`Unexpected operator "${op}"`);
  }
}

export type DiscreteValueReportAggregateFunction =
  | 'min'
  | 'max'
  | 'last-created'
  | 'last-started'
  | 'last-ended'
  | 'first-created'
  | 'first-started'
  | 'first-ended';

export type ReportAggregateFunction =
  | DiscreteValueReportAggregateFunction
  | 'count'
  | 'count-distinct'
  | 'sum'
  | 'avg'
  | 'median'
  | 'stdev'
  | 'var';

export const aggregateFunctions: ReportAggregateFunction[] = [
  'count',
  'count-distinct',
  'sum',
  'min',
  'max',
  'avg',
  'median',
  'stdev',
  'var',
  'last-created',
  'last-started',
  'last-ended',
  'first-created',
  'first-started',
  'first-ended'
];

export const aggregateFunctionResultType: Record<
  ReportAggregateFunction,
  'int' | 'decimal' | 'inherit-number' | 'inherit'
> = {
  count: 'int',
  'count-distinct': 'int',
  sum: 'inherit-number',
  min: 'inherit',
  max: 'inherit',
  avg: 'decimal',
  median: 'decimal',
  stdev: 'decimal',
  var: 'decimal',
  'last-created': 'inherit',
  'last-started': 'inherit',
  'last-ended': 'inherit',
  'first-created': 'inherit',
  'first-started': 'inherit',
  'first-ended': 'inherit'
};

const discreteValueAggregateFunctions: ReportAggregateFunction[] = [
  'min',
  'max',
  'last-created',
  'last-started',
  'last-ended',
  'first-created',
  'first-started',
  'first-ended'
] satisfies DiscreteValueReportAggregateFunction[];

export function isDiscreteValueAggregateFunction(
  value: ReportAggregateFunction
): value is DiscreteValueReportAggregateFunction {
  return discreteValueAggregateFunctions.includes(value);
}

const inheritTypeAggregateFunctions: ReportAggregateFunction[] = [
  'sum',
  'min',
  'max',
  'last-created',
  'last-started',
  'last-ended',
  'first-created',
  'first-started',
  'first-ended'
];

export function isInheritTypeAggregateFunction(
  value: ReportAggregateFunction
): value is
  | 'sum'
  | 'min'
  | 'max'
  | 'last-created'
  | 'last-started'
  | 'last-ended'
  | 'first-created'
  | 'first-started'
  | 'first-ended' {
  return inheritTypeAggregateFunctions.includes(value);
}

export const aggregateFunctionSort: Record<
  | 'last-created'
  | 'last-started'
  | 'last-ended'
  | 'first-created'
  | 'first-started'
  | 'first-ended',
  (alias: string) => string
> = {
  'last-created': alias => `${alias}.[create_datetime] desc`,
  'last-started': alias => `${alias}.[start_date] desc`,
  'last-ended': alias => `${alias}.[end_date] desc, ${alias}.[start_date] desc`,
  'first-created': alias => `${alias}.[create_datetime] asc`,
  'first-started': alias => `${alias}.[start_date] asc`,
  'first-ended': alias => `${alias}.[end_date] asc, ${alias}.[start_date] asc`
};

export const choicePropertyAggregateFunctions: ReportAggregateFunction[] = [
  'count-distinct',
  'sum',
  'avg',
  'median',
  'stdev',
  'var',
  'min',
  'max',
  'last-created',
  'last-started',
  'last-ended',
  'first-created',
  'first-started',
  'first-ended'
];

export const choicePropertyScoreAggregateFunctions: ReportAggregateFunction[] =
  ['sum', 'avg', 'median', 'stdev', 'var', 'min', 'max'];

export const aggregateFunctionPropertyTypes: Record<
  ReportAggregateFunction,
  PropertyType[]
> = {
  count: [],
  'count-distinct': [
    'uniqueidentifier',
    'varchar100',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  sum: ['int', 'currency', 'decimal'],
  avg: ['int', 'currency', 'decimal'],
  median: ['int', 'currency', 'decimal'],
  stdev: ['int', 'currency', 'decimal'],
  var: ['int', 'currency', 'decimal'],
  min: [
    'uniqueidentifier',
    'varchar100',
    'int',
    'bit',
    'currency',
    'decimal',
    'date'
  ],
  max: [
    'uniqueidentifier',
    'varchar100',
    'int',
    'bit',
    'currency',
    'decimal',
    'date'
  ],
  'last-created': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  'last-started': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  'last-ended': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  'first-created': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  'first-started': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ],
  'first-ended': [
    'uniqueidentifier',
    'varchar100',
    'varcharmax',
    'int',
    'bit',
    'currency',
    'decimal',
    'date',
    'activity'
  ]
};

export const aggregateFunctionNames: Record<ReportAggregateFunction, string> = {
  count: 'Count',
  'count-distinct': 'Distinct count',
  sum: 'Sum',
  min: 'Minimum',
  max: 'Maximum',
  avg: 'Average',
  median: 'Median',
  stdev: 'Standard deviation',
  var: 'Variance',
  'last-created': 'Most recently created',
  'last-started': 'Most recently started',
  'last-ended': 'Most recently ended',
  'first-created': 'Earliest created',
  'first-started': 'Earliest started',
  'first-ended': 'Earliest ended'
};

export const compositeProperties = [
  // custom:
  wellKnownProperties.activity.attachment_count,
  wellKnownProperties.activity.assignee_id,
  wellKnownProperties.activity.create_dow,
  wellKnownProperties.activity.subscribed,
  wellKnownProperties.activity.comment_create_user_id,
  wellKnownProperties.activity.comment_mentioned_user_id,
  wellKnownProperties.activity.primary_physical.is_precise,
  wellKnownProperties.activity.assignee_organization_primary_physical
    .is_precise,
  wellKnownProperties.activity.create_organization_primary_physical.is_precise,
  wellKnownProperties.person.primary_mailing.is_precise,
  wellKnownProperties.person.primary_physical.is_precise,
  wellKnownProperties.person.primary_mailing.is_precise,
  wellKnownProperties.user_organization.projects,
  wellKnownProperties.organization_project.primary_physical.is_precise,
  // composite:
  wellKnownProperties.activity.primary_physical.address,
  wellKnownProperties.activity.assignee_organization_primary_physical.address,
  wellKnownProperties.activity.create_organization_primary_physical.address,
  wellKnownProperties.activity.assignee_organization_primary_email.formatted,
  wellKnownProperties.activity.create_organization_primary_email.formatted,
  wellKnownProperties.activity.primary_phone.formatted,
  wellKnownProperties.activity.primary_email.formatted,
  wellKnownProperties.person.primary_physical.address,
  wellKnownProperties.person.primary_mailing.address,
  wellKnownProperties.person.primary_phone.formatted,
  wellKnownProperties.person.primary_email.formatted,
  wellKnownProperties.user_organization.primary_phone.formatted,
  wellKnownProperties.organization_project.primary_phone.formatted,
  wellKnownProperties.organization_project.primary_physical.address
];

export function getGroupingOptions(
  column:
    | Pick<StandardReportColumn | ExpressionReportColumn, 'type'>
    | Pick<SubqueryReportColumn, 'type' | 'aggregate'>,
  property: Pick<
    Property,
    'property_id' | 'type' | 'choice_type' | 'response'
  > | null,
  expressionType: ExpressionReturnType | null
): ('none' | 'group' | ReportAggregateFunction)[] {
  // no grouping for composite props or properties whose choice type is not 'single' or 'none'.
  if (
    compositeProperties.includes(property?.property_id ?? NaN) ||
    (property?.response &&
      !(
        ['none', 'single', 'multiple'].includes(property.choice_type) ||
        property.type === 'activity'
      ))
  ) {
    return ['none'];
  }
  // grouping only for activity properties.
  if (property?.type === 'activity') {
    return ['none', 'group'];
  }
  if (property?.choice_type === 'single') {
    return ['none', 'group', ...choicePropertyAggregateFunctions];
  }
  if (property?.choice_type === 'multiple') {
    return ['none', ...choicePropertyAggregateFunctions];
  }
  if (
    (property && column.type === 'standard') ||
    (property &&
      column.type === 'subquery' &&
      discreteValueAggregateFunctions.includes(column.aggregate))
  ) {
    switch (property.type) {
      case 'date':
        return ['none', 'group', 'count', 'count-distinct', 'max', 'min'];
      case 'currency':
      case 'int':
      case 'decimal':
        return [
          'none',
          'group',
          'count',
          'count-distinct',
          'sum',
          'avg',
          'median',
          'stdev',
          'var',
          'max',
          'min'
        ];
      case 'varchar100':
        return ['none', 'group', 'count', 'count-distinct', 'max', 'min'];
      case 'varcharmax':
        return ['none'];
      case 'bit':
        return ['none', 'group'];
      case 'uniqueidentifier':
        return ['none', 'group', 'count', 'count-distinct', 'max', 'min'];
      default:
        return ['none'];
    }
  } else if (column.type === 'subquery') {
    switch (column.aggregate) {
      case 'sum':
      case 'count':
      case 'count-distinct':
      case 'avg':
      case 'median':
      case 'stdev':
      case 'var':
        return ['none', 'sum', 'avg', 'median', 'stdev', 'var', 'max', 'min'];
      default:
        return ['none'];
    }
  } else if (column.type === 'expression') {
    switch (expressionType) {
      case 'date':
      case 'datetime':
        return ['none', 'group', 'max', 'min'];
      case 'int':
      case 'decimal':
        return [
          'none',
          ...(expressionType === 'int' ? ['group' as 'group'] : []),
          'sum',
          'avg',
          'median',
          'stdev',
          'var',
          'max',
          'min'
        ];
      case 'string':
        return ['none', 'group'];
      case 'boolean':
      default:
        return ['none'];
    }
  } else {
    return ['none'];
  }
}

export type BlankType = 'empty-string' | 'null' | 'null-or-empty-string';

export const blankTypes = /*@__PURE__*/ new Map<number, BlankType>([
  [wellKnownProperties.person.first_name, 'empty-string'],
  [wellKnownProperties.person.middle_name, 'empty-string'],
  [wellKnownProperties.person.suffix, 'empty-string'],
  [wellKnownProperties.person.nickname, 'empty-string'],
  [wellKnownProperties.person.gender, 'null'],
  [wellKnownProperties.person.gender_identity, 'null'],
  [wellKnownProperties.person.sexual_orientation, 'null'],
  [wellKnownProperties.person.race, 'null'],
  [wellKnownProperties.person.ethnicity, 'null'],
  [wellKnownProperties.person.marital_status, 'null'],
  [wellKnownProperties.person.primary_language, 'null'],
  [wellKnownProperties.person.dob, 'null'],
  [wellKnownProperties.person.primary_phone.number, 'null'],
  [wellKnownProperties.person.primary_phone.extension, 'null-or-empty-string'],
  [
    wellKnownProperties.person.primary_physical.street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_physical.sub_address,
    'null-or-empty-string'
  ],
  [wellKnownProperties.person.primary_physical.city, 'null-or-empty-string'],
  [
    wellKnownProperties.person.primary_physical.region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_physical.subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_physical.postal_code,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_mailing.street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_mailing.sub_address,
    'null-or-empty-string'
  ],
  [wellKnownProperties.person.primary_mailing.city, 'null-or-empty-string'],
  [
    wellKnownProperties.person.primary_mailing.region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_mailing.subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.person.primary_mailing.postal_code,
    'null-or-empty-string'
  ],
  [wellKnownProperties.person.primary_email.email, 'null'],
  [wellKnownProperties.person.ssn, 'null'],
  [wellKnownProperties.person.veteran_id, 'null'],
  [wellKnownProperties.person.medicaid_id, 'null'],
  [wellKnownProperties.person.medicare_beneficiary_id, 'null'],
  [wellKnownProperties.person.agingis_id, 'null'],
  [wellKnownProperties.person.scsep_participant_id, 'null'],
  [wellKnownProperties.person.user_id, 'null'],
  [wellKnownProperties.person.user_email_address, 'null'],
  [wellKnownProperties.person.open_participant_portal_invitation, 'null'],
  [wellKnownProperties.activity.parent_activity_id, 'null'],
  [wellKnownProperties.activity.subject_person_id, 'null'],
  [wellKnownProperties.activity.end_date, 'null'],
  [wellKnownProperties.activity.due_date, 'null'],
  [wellKnownProperties.activity.assignee_user_id, 'null'],
  [wellKnownProperties.activity.service_authorization_service_id, 'null'],
  [wellKnownProperties.activity.primary_phone.number, 'null'],
  [
    wellKnownProperties.activity.primary_phone.extension,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.primary_physical.street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.primary_physical.sub_address,
    'null-or-empty-string'
  ],
  [wellKnownProperties.activity.primary_physical.city, 'null-or-empty-string'],
  [
    wellKnownProperties.activity.primary_physical.region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.primary_physical.subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.primary_physical.postal_code,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical
      .street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical
      .sub_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical.city,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical
      .region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical
      .subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.assignee_organization_primary_physical
      .postal_code,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical
      .street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical
      .sub_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical.city,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical
      .region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical.subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.activity.create_organization_primary_physical
      .postal_code,
    'null-or-empty-string'
  ],
  [wellKnownProperties.organization_project.classification, 'null'],
  [wellKnownProperties.organization_project.fein, 'null'],

  [wellKnownProperties.organization_project.primary_phone.number, 'null'],
  [
    wellKnownProperties.organization_project.primary_phone.extension,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.street_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.sub_address,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.city,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.region_abbr,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.subregion,
    'null-or-empty-string'
  ],
  [
    wellKnownProperties.organization_project.primary_physical.postal_code,
    'null-or-empty-string'
  ]
]);

export function getBlankType(property: {
  property_id: number;
  response?: boolean;
}): BlankType | undefined {
  if (property.response) {
    return 'null';
  }
  return blankTypes.get(property.property_id);
}

export const reportLimits = {
  parameters: 30,
  filters: 15,
  sorts: 4,
  columns: 35,
  inItems: 75,
  shares: 20,
  filtersOnSameField: 8,
  expressionMaxLength: 100,
  expressionMaxExpandedNodes: 256,
  expressionMaxExpandedDepth: 32,
  expressionMaxReferenceDepth: 16,
  expressionMaxGeneratedSqlLength: 32768,
  parameterSelectItems: 24,
  parameterNameMaxLength: 30,
  parameterLabelMaxLength: 20,
  parameterDescriptionMaxLength: 200
};

export const reportWildcards = {
  me: {
    id: 'b0000000-0000-0000-0000-000000000000',
    title: '@Me'
  },
  myOrganization: {
    id: 'b0000000-0000-0000-0000-000000000001',
    title: '@My Organization'
  },
  createUser: {
    id: 'b0000000-0000-0000-0000-000000000002',
    title: '@Creator'
  },
  createOrganization: {
    id: 'b0000000-0000-0000-0000-000000000003',
    title: '@Creator Organization'
  },
  allUsers: {
    id: 'b0000000-0000-0000-0000-000000000004',
    title: '@All Users'
  },
  allOrganizations: {
    id: 'b0000000-0000-0000-0000-000000000005',
    title: '@All Organizations'
  },
  allActivities: {
    id: 'b0000000-0000-0000-0000-000000000006',
    title: '@All Activities'
  },
  allProjects: {
    id: 'b0000000-0000-0000-0000-000000000007',
    title: '@All Projects'
  },
  allChoices: {
    id: -1,
    title: '@All Choices'
  },
  unansweredChoice: {
    id: -2,
    title: '@Unanswered'
  },
  myOrganizationCity: {
    id: '@MyOrganizationCity',
    title: `@My Organization's City`
  },
  myOrganizationState: {
    id: '@MyOrganizationState',
    title: `@My Organization's State`
  },
  myOrganizationCounty: {
    id: '@MyOrganizationCounty',
    title: `@My Organization's County`
  },
  myOrganizationContracts: {
    id: '@MyOrganizationContracts',
    title: `@My Organization's Contracts`
  }
};

export const reportFilterNeverMatchUuid =
  'ffffffff-eeee-dddd-cccc-aaaaaaaaaaaa';

export function isReportReadOnly(
  report: Report,
  userOrganization: {
    user_id: string;
    role_id: string;
    organization_id: string;
  }
) {
  if (
    report.create_user_id === userOrganization.user_id &&
    report.create_organization_id === userOrganization.organization_id &&
    hasRoleLevel(userOrganization, 'Writer')
  ) {
    return false;
  }
  if (
    report.is_shared !== ReportSharedWith.None &&
    userOrganization.organization_id === report.create_organization_id &&
    hasRoleLevel(userOrganization, 'Admin')
  ) {
    return false;
  }
  return true;
}

export type ReportSubqueryAssociation =
  | 'root-activity'
  | 'root-descendant-activities'
  | 'grandparent-activity'
  | 'parent-activity'
  | 'child-activities'
  | 'grandchild-activities'
  | 'referenced-activities'
  | 'second-degree-referenced-activities'
  | 'referenced-root-descendant-activities'
  | 'referencing-activities'
  | 'person-activities'
  | 'sibling-activities'
  | 'assigned-activities'
  | 'created-activities'
  | 'contracted-activities'
  | 'organization-project-users'
  | ReportEntityType;

export const reportSubqueryAssociations: ReportSubqueryAssociation[] = [
  'root-activity',
  'root-descendant-activities',
  'grandparent-activity',
  'parent-activity',
  'child-activities',
  'grandchild-activities',
  'referenced-activities',
  'second-degree-referenced-activities',
  'referenced-root-descendant-activities',
  'referencing-activities',
  'person-activities',
  'sibling-activities',
  'assigned-activities',
  'created-activities',
  'contracted-activities',
  'organization-project-users',
  'activity',
  'person',
  'user_organization',
  'organization_project'
];

export function isReportSubqueryAncestorAssociation(
  association: unknown
): association is 'root-activity' | 'grandparent-activity' | 'parent-activity' {
  return (
    association === 'root-activity' ||
    association === 'grandparent-activity' ||
    association === 'parent-activity'
  );
}

export const reportSubqueryAssociationToEntityType: Record<
  ReportSubqueryAssociation,
  ReportEntityType
> = {
  'root-activity': 'activity',
  'root-descendant-activities': 'activity',
  'grandparent-activity': 'activity',
  'parent-activity': 'activity',
  'child-activities': 'activity',
  'grandchild-activities': 'activity',
  'referenced-activities': 'activity',
  'second-degree-referenced-activities': 'activity',
  'referenced-root-descendant-activities': 'activity',
  'referencing-activities': 'activity',
  'person-activities': 'activity',
  'sibling-activities': 'activity',
  'assigned-activities': 'activity',
  'created-activities': 'activity',
  'contracted-activities': 'activity',
  'organization-project-users': 'user_organization',
  activity: 'activity',
  person: 'person',
  user_organization: 'user_organization',
  organization_project: 'organization_project'
};

export type ReportSubquery =
  | {
      type: 'subquery';
      association: ReportSubqueryAssociation;
      aggregate: 'count';
      filters: ReportFilter[];
    }
  | {
      type: 'subquery';
      association: ReportSubqueryAssociation;
      aggregate: Exclude<ReportAggregateFunction, 'count'>;
      subquery_property_id: number;
      filters: ReportFilter[];
    };

export type ReportFilterValue = null | string | number | boolean | LookupItem[];

export type ReportFilter = StandardReportFilter | SubqueryReportFilter;

export interface StandardReportFilter {
  type: 'standard';
  property_id: number;
  operator: ReportFilterOperator;
  value: ReportFilterValue;
}

export type SubqueryReportFilter = ReportSubquery & {
  operator: ReportFilterOperator;
  value: ReportFilterValue;
};
export interface ReportSort {
  column_index: number;
  direction: ReportSortDirection;
}

export type ReportColumn =
  StandardReportColumn | SubqueryReportColumn | ExpressionReportColumn;

export interface ReportColumnGrouping {
  type: 'none' | 'group' | ReportAggregateFunction;
}

export type ReportColumnSort = `${0 | 1 | 2 | 3}/${'asc' | 'desc'}`;

export function sequenceSorts(columns: ReportColumn[]) {
  const useGrouping = columns.some(c => c.grouping.type !== 'none');
  if (useGrouping) {
    for (const column of columns) {
      if (column.grouping.type === 'none') {
        column.sort = null;
      }
    }
  }
  columns
    .filter(x => x.sort)
    .sort((a, b) => +a.sort![0] - +b.sort![0])
    .slice(0, 4)
    .forEach((x, i) => {
      x.sort = `${i as 0 | 1 | 2 | 3}/${x.sort!.split('/')[1] as ReportSortDirection}`;
    });
  if (!columns.some(x => x.sort)) {
    const first = columns.find(x => !useGrouping || x.grouping.type !== 'none');
    if (first) {
      first.sort = '0/asc';
    }
  }
}

export const reportColumnSortOptions: {
  value: '' | ReportColumnSort;
  text: string;
  group?: string;
}[] = [
  { value: '', text: 'None' },
  { value: '0/asc', text: 'Ascending (1)', group: 'Primary sort' },
  { value: '0/desc', text: 'Descending (1)', group: 'Primary sort' },
  { value: '1/asc', text: 'Ascending (2)', group: 'Secondary sort' },
  { value: '1/desc', text: 'Descending (2)', group: 'Secondary sort' },
  { value: '2/asc', text: 'Ascending (3)', group: 'Third sort' },
  { value: '2/desc', text: 'Descending (3)', group: 'Third sort' },
  { value: '3/asc', text: 'Ascending (4)', group: 'Fourth sort' },
  { value: '3/desc', text: 'Descending (4)', group: 'Fourth sort' }
];

export interface ReportColumnBase {
  label?: string;
  grouping: ReportColumnGrouping;
  sort: ReportColumnSort | null;
}

export type StandardReportColumn = ReportColumnBase & {
  type: 'standard';
  property_id: number;
};

export type SubqueryReportColumn = ReportColumnBase & ReportSubquery;

export type ExpressionReportColumn = ReportColumnBase & {
  type: 'expression';
  expression: string;
};

export function isReportColumnHidden(label: string): boolean {
  return label.toLowerCase().includes('[hidden]');
}

export function reportColumnHeader(
  index: number,
  column: ReportColumn,
  entity_type: ReportEntityType,
  properties: Record<number, Property>
) {
  if (column.type === 'subquery' || column.type === 'expression') {
    return column.label || `Column ${reportColumnIndexToIdentifier(index)}`;
  }

  const property = properties[column.property_id];
  return column.label || getPropertyLabelForReport(entity_type, property);
}

export const identifierRegex = /^\$[A-Z][A-Z0-9_]{0,28}$/;
export const internalReportParameterNameRegex = /^\$RP\d+_/;

export interface DeprecatedLiteralReportParameter {
  type: 'literal';
  name: string;
  label: string;
  data_type: 'date';
  value: string;
}

export type ReportParameter =
  | ExpressionReportParameter
  | LiteralReportParameter
  | ActivityReportParameter
  | UserReportParameter
  | OrganizationReportParameter
  | ProjectReportParameter
  | ChoiceReportParameter
  | SubqueryReportParameter
  | ReportBackedSubqueryReportParameter;

export type ExpressionReportParameter = {
  type: 'expression';
  name: string;
  label: string;
  expression: string;
};

export type LiteralReportParameter =
  | {
      type: 'literal-date';
      name: string;
      label: string;
      value: string;
    }
  | {
      type: 'month-date';
      name: string;
      label: string;
      value: string;
    }
  | {
      type: 'literal-number';
      name: string;
      label: string;
      value: number;
    }
  | {
      type: 'select-date';
      name: string;
      label: string;
      values: {
        value: string;
        label: string;
      }[];
    }
  | {
      type: 'select-number';
      name: string;
      label: string;
      values: {
        value: number;
        label: string;
      }[];
    };

export type UserReportParameter = {
  type: 'user';
  name: string;
  label: string;
  // constraint: 'all' | 'contracted' | 'directly-contracted';
  value: LookupItem[];
};

export type ActivityReportParameter = {
  type: 'activity';
  name: string;
  label: string;
  activity_definition_id: string;
  value: LookupItem[];
};

export const reportActivityParameterTypes = [
  'organization-event',
  'organization-agency',
  'organization-site',
  'organization-period'
] as const satisfies readonly ActivityType[];

export type ReportActivityParameterType =
  (typeof reportActivityParameterTypes)[number];

export type OrganizationReportParameter = {
  type: 'organization';
  name: string;
  label: string;
  // constraint: 'all' | 'contracted' | 'directly-contracted';
  value: LookupItem[];
};

export type ProjectReportParameter = {
  type: 'project';
  name: string;
  label: string;
  value: LookupItem[];
};

export type ChoiceReportParameter = {
  type: 'choice';
  name: string;
  label: string;
  property_id: number;
  value: LookupItem[];
};

export type SubqueryReportParameter = {
  type: 'subquery';
  name: string;
  label: string;
  description: string;
} & ReportSubquery;

export type ReportBackedSubqueryReportParameter =
  | {
      type: 'report-subquery';
      name: string;
      label: string;
      description: string;
      report_id: string;
      aggregate: 'count';
      subquery_property_id: null;
    }
  | {
      type: 'report-subquery';
      name: string;
      label: string;
      description: string;
      report_id: string;
      aggregate: Exclude<ReportAggregateFunction, 'count'>;
      subquery_property_id: number;
    };

export function reportParameterIsCalculated(
  parameter: ReportParameter
): parameter is
  | ExpressionReportParameter
  | SubqueryReportParameter
  | ReportBackedSubqueryReportParameter {
  return (
    parameter.type === 'expression' ||
    parameter.type === 'subquery' ||
    parameter.type === 'report-subquery'
  );
}

export const reportParameterTypes: ReportParameter['type'][] = [
  'expression',
  'literal-date',
  'month-date',
  'literal-number',
  'select-date',
  'select-number',
  'activity',
  'user',
  'organization',
  'project',
  'choice',
  'subquery',
  'report-subquery'
];

export const reportParameterTypeNames: Record<ReportParameter['type'], string> =
  {
    expression: 'Formula',
    'literal-date': 'Date',
    'month-date': 'Month',
    'literal-number': 'Number',
    'select-date': 'Date pick list',
    'select-number': 'Number pick list',
    activity: 'Activities',
    user: 'Users',
    organization: 'Organizations',
    project: 'Projects',
    choice: 'Choice question',
    subquery: 'Data lookup',
    'report-subquery': 'Report lookup'
  };

export interface ReportDefinition {
  parameters: ReportParameter[];
  filters: ReportFilter[];
  columns: ReportColumn[];
  dashboard_elements: DashboardElement[];
}

export type ReportShareItem =
  | {
      organization_id: string;
      user_id: null;
      user_organization_id: null;
      title: string;
    }
  | {
      organization_id: string;
      user_id: string;
      user_organization_id: string;
      title: string;
    };

export interface ReportCore {
  layout: ReportLayout;
  entity_type: ReportEntityType;
  project_id: string | null;
  title: string;
  description: string;
  is_shared: ReportSharedWith;
  definition: ReportDefinition;
}

export type ReportParameterValues = Record<
  string,
  string | number | LookupItem[]
>;

export interface PostReportArgs extends ReportCore {
  parameter_values?: ReportParameterValues;
  shares: LookupItem[] | null;
}
export interface Report extends ReportCore, SharedResource {
  report_id: string;
  shares: ReportShareItem[];
}

export interface ReportListItem extends Omit<Report, 'definition'> {
  report_id: string;
  definition?: ReportDefinition;
  is_subscribed: boolean;
  subscriber_count: number;
  total_view_count_30_days: number;
  user_view_count_30_days: number;
  user_last_viewed: string | null;
  project_name: string | null;
  project_login: string | null;

  create_datetime: string;
  create_user_id: string;
  create_user_login: string;
  create_user_name: string;
  create_user_avatar_id: string | null;
  create_user_is_active: boolean;
  create_user_is_ooo: boolean;
  create_organization_id: string;
  create_organization_login: string;
  create_organization_name: string;
  create_organization_avatar_id: string | null;
  update_datetime: string;

  shares: ReportShareItem[];
}

export interface ReportSupportingData {
  report: Report | ReportCore;
  associatedReports?: ReportListItem[];
  properties: Property[];
  userOrganization: UserOrganizationListItem;
  organizationPrimaryPhysicalAddress: Pick<
    AddressFields,
    'city' | 'subregion' | 'region_abbr'
  > | null;
}

export interface ReportDefaults {
  columns: ReportColumn[];
  filters: ReportFilter[];
  sorts: ReportSort[];
}

export interface ReportSubscription extends OrganizationSharedResource {
  report_subscription_id: string;
  report_id: string;
  user_id: string;
  days: number;
  time: number;
  iana_timezone: string;
  send_zero: boolean;
}

export enum ReportSubscriptionJobState {
  Scheduled = 0,
  Processing = 1,
  Complete = 2,
  Exception = 3,
  Expired = 4
}

export interface UpdateReportSubscriptionArgs {
  days: number;
  time: number;
  send_zero: boolean;
}

export interface ReportRowsLoadedPayload {
  rows: unknown[];
  count: number;
  parameter_values: Record<string, string | number | null>;
}

export const reportDefaults: Record<ReportEntityType, ReportDefinition> = {
  activity: {
    parameters: [],
    columns: [
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.activity_definition_id,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.status,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.assignee_organization_id,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.assignee_user_id,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.start_date,
        grouping: { type: 'none' },
        sort: '0/desc'
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.end_date,
        grouping: { type: 'none' },
        sort: null
      }
    ],
    filters: [
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.status,
        operator: 'in',
        value: openActivityStatuses.map(id => ({
          id,
          title: activityStatusTitles[id][defaultLocale]
        }))
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.activity.assignee_user_id,
        operator: 'in',
        value: [reportWildcards.me]
      }
    ],
    dashboard_elements: []
  },
  person: {
    parameters: [],
    columns: [
      {
        type: 'standard',
        property_id: wellKnownProperties.person.full_name,
        grouping: { type: 'none' },
        sort: '0/asc'
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.person.dob,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.person.gender,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.person.primary_physical.address,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.person.create_user_id,
        grouping: { type: 'none' },
        sort: null
      }
    ],
    filters: [
      {
        type: 'standard',
        property_id: wellKnownProperties.person.create_organization_id,
        operator: 'in',
        value: [reportWildcards.myOrganization]
      }
    ],
    dashboard_elements: []
  },
  user_organization: {
    parameters: [],
    columns: [
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.organization_id,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.login,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.family_name,
        grouping: { type: 'none' },
        sort: '0/asc'
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.given_name,
        grouping: { type: 'none' },
        sort: '1/asc'
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.email,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id:
          wellKnownProperties.user_organization.primary_phone.formatted,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.projects,
        grouping: { type: 'none' },
        sort: null
      }
    ],
    filters: [
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.organization_id,
        operator: 'in',
        value: [reportWildcards.myOrganization]
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.user_organization.is_active,
        operator: '=',
        value: true
      }
    ],
    dashboard_elements: []
  },
  organization_project: {
    parameters: [],
    columns: [
      {
        type: 'standard',
        property_id: wellKnownProperties.organization_project.organization_id,
        grouping: { type: 'none' },
        sort: '0/asc'
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.organization_project.project_id,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id: wellKnownProperties.organization_project.project_join_date,
        grouping: { type: 'none' },
        sort: null
      },
      {
        type: 'standard',
        property_id:
          wellKnownProperties.organization_project.primary_physical.region_abbr,
        grouping: { type: 'none' },
        sort: null
      }
    ],
    filters: [
      {
        type: 'standard',
        property_id: wellKnownProperties.organization_project.is_active,
        operator: '=',
        value: true
      }
    ],
    dashboard_elements: []
  }
};

export const defaultDashboardReportDefinition: () => ReportDefinition = () => ({
  parameters: [
    {
      type: 'literal-date',
      name: '$START_DATE',
      label: 'Start Date',
      value: `${new Date().getFullYear()}-01-01`
    },
    {
      type: 'literal-date',
      name: '$END_DATE',
      label: 'End Date',
      value: `${new Date().getFullYear()}-12-31`
    }
  ],
  columns: [
    {
      type: 'standard',
      property_id: wellKnownProperties.activity.start_date,
      grouping: { type: 'none' },
      sort: '0/asc'
    }
  ],
  filters: [
    {
      type: 'standard',
      property_id: wellKnownProperties.activity.start_date,
      operator: '<',
      value: new Date(minDate).toISOString().substr(0, 10)
    }
  ],
  dashboard_elements: []
});

export function getReportReferencedPropertyIds(
  definition: ReportDefinition
): number[] {
  const ids = new Set<number>();
  const add = (item: ReportFilter | ReportColumn | ReportParameter) => {
    if (item.type === 'subquery') {
      if (item.aggregate !== 'count') {
        ids.add(item.subquery_property_id);
      }
      item.filters?.forEach(add);
    } else if (item.type === 'report-subquery') {
      if (item.aggregate !== 'count') {
        ids.add(item.subquery_property_id);
      }
    } else if (item.type === 'choice') {
      ids.add(item.property_id);
    } else if (item.type === 'standard') {
      ids.add(item.property_id);
    }
  };
  definition.filters.forEach(add);
  definition.columns.forEach(add);
  definition.parameters.forEach(add);
  return Array.from(ids).sort();
}

export function getReportBackedSubqueryReferencedReportIds(
  definition: ReportDefinition
): string[] {
  return definition.parameters
    .filter(p => p.type === 'report-subquery')
    .map(p => p.report_id)
    .filter((x): x is string => typeof x === 'string' && x !== '')
    .filter((x, i, a) => a.indexOf(x) === i);
}

export function getReportReferencedReportIds(
  definition: ReportDefinition
): string[] {
  const ids = definition.dashboard_elements
    .map(x =>
      x.type === 'report'
        ? x.report_id
        : x.type === 'metric'
          ? [x.value_report_id, x.goal_report_id]
          : x.type === 'report-links'
            ? x.reports.map(r => r.report_id)
            : null
    )
    .flat()
    .filter((x): x is string => typeof x === 'string' && x !== '')
    .concat(getReportBackedSubqueryReferencedReportIds(definition));
  return ids.filter((x, i, a) => a.indexOf(x) === i);
}

export function getReportBackedSubqueryReferencedReports(
  definition: ReportDefinition,
  associatedReports: ReportListItem[]
): ReportListItem[] {
  const reports = new Map(associatedReports.map(r => [r.report_id, r]));
  const referencedReportIds = new Set<string>();
  const visit = (definition: ReportDefinition) => {
    for (const report_id of getReportBackedSubqueryReferencedReportIds(
      definition
    )) {
      if (referencedReportIds.has(report_id)) {
        continue;
      }
      referencedReportIds.add(report_id);
      const report = reports.get(report_id);
      if (report?.definition) {
        visit(report.definition);
      }
    }
  };
  visit(definition);
  return associatedReports.filter(r => referencedReportIds.has(r.report_id));
}

export function getPropertyLabelForReport(
  entity: ReportEntityType,
  property: Pick<Property, 'label' | 'response' | 'entity_type' | 'type'>
) {
  let label = property.label;
  if (property.response || property.entity_type === entity) {
    return label;
  }
  if (!/^[A-Z][A-Z]/.test(label)) {
    label = label.toLowerCase();
  }
  const entitySingular =
    reportEntityInfo[property.entity_type as ReportEntityType].singular[
      defaultLocale
    ];
  return `${entitySingular} ${label}`;
}

export function getReportFilterOperators(
  filter:
    | Pick<StandardReportFilter, 'type'>
    | Pick<SubqueryReportFilter, 'type' | 'aggregate'>,
  property: Pick<Property, 'property_id' | 'type' | 'choice_type'> | null
): ReportFilterOperator[] {
  const aggregate = filter.type === 'standard' ? 'max' : filter.aggregate;
  const resultType = aggregateFunctionResultType[aggregate];
  switch (resultType) {
    case 'decimal':
    case 'int':
    case 'inherit-number':
      return [...comparisonOperators];
    case 'inherit': {
      if (!property) {
        return [];
      }
      let ops: ReportFilterOperator[];
      switch (property.type) {
        case 'signature':
        case 'medications':
        case 'attachment':
          ops = [];
          break;
        case 'currency':
        case 'date':
        case 'decimal':
        case 'int':
          if (property.choice_type === 'none') {
            ops = ['=', '<', '<=', '>', '>='];
          } else {
            ops = ['in', 'not in'];
          }
          break;
        case 'varchar100':
          if (property.choice_type === 'none') {
            ops = [
              '=',
              '<',
              '<=',
              '>',
              '>=',
              'startswith',
              'notstartswith',
              'contains',
              'notcontains'
            ];
          } else {
            ops = ['in', 'not in'];
          }
          break;
        case 'varcharmax':
          ops = ['=', 'startswith', 'notstartswith', 'contains', 'notcontains'];
          break;
        case 'uniqueidentifier':
          if (property.choice_type === 'none') {
            throw new Error(
              `Expected uniqueidentifier property to have choice_type ("${property.property_id}").`
            );
          }
          ops = ['in', 'not in'];
          break;
        case 'bit':
          ops = ['='];
          break;
        case 'activity':
          ops = ['in', 'not in'];
          break;
        default:
          throw new Error(`Unsupported property type "${property.type}".`);
      }
      if (getBlankType(property)) {
        ops.push('blank', 'notblank');
      }
      return ops;
    }
    default:
      throw new Error(
        `Unexpected aggregate function result type "${resultType}".`
      );
  }
}

export type ReportFilterValueTypeInfo =
  | { type: PropertyType; choice_type: 'none' }
  | Pick<Property, 'property_id' | 'type' | 'choice_type'>;

export function getReportFilterValueType(
  filter: ReportFilter,
  property: Pick<Property, 'property_id' | 'type' | 'choice_type'> | null,
  operator: ReportFilterOperator
): ReportFilterValueTypeInfo | 'blank' | null {
  const aggregate = filter.type === 'standard' ? 'max' : filter.aggregate;
  const resultType = aggregateFunctionResultType[aggregate];
  switch (resultType) {
    case 'decimal':
    case 'int':
      return { type: resultType, choice_type: 'none' };
    case 'inherit-number':
    case 'inherit': {
      if (!property) {
        return null;
      }
      switch (property.type) {
        case 'medications':
        case 'signature':
        case 'attachment':
          if (operator === 'blank' || operator === 'notblank') {
            return 'blank';
          }
          return null;
        case 'bit':
        case 'currency':
        case 'date':
        case 'decimal':
        case 'int':
        case 'varchar100':
        case 'varcharmax':
        case 'uniqueidentifier':
        case 'activity':
          if (operator === 'blank' || operator === 'notblank') {
            return 'blank';
          }
          return property;
        default:
          throw new Error(`Unsupported property type "${property.type}".`);
      }
    }
    default:
      throw new Error(
        `Unexpected aggregate function result type "${resultType}".`
      );
  }
}

export const reportGridColumnDefTypes = {
  single_choice: 'single_choice',
  multiple_choice: 'multiple_choice',
  boolean: 'boolean',
  date: 'date',
  decimal: 'decimal',
  currency: 'currency',
  int: 'int',
  row_href: 'row_href',
  composite_address: 'composite_address',
  composite_email: 'composite_email',
  composite_phone: 'composite_phone',
  value_map: 'value_map',
  link: 'link',
  time: 'time'
};

export function getSubqueryParameterType(
  parameter: SubqueryReportParameter | ReportBackedSubqueryReportParameter,
  properties: Property[]
): { type: ExpressionReturnType | null; property?: Property } {
  if (parameter.aggregate === 'count') {
    return { type: 'int' };
  }
  const aggregateResultType = aggregateFunctionResultType[parameter.aggregate];
  switch (aggregateResultType) {
    case 'decimal':
      return { type: 'decimal' };
    case 'int':
      return { type: 'int' };
    case 'inherit-number':
    case 'inherit': {
      const property = properties.find(
        p => p.property_id === parameter.subquery_property_id
      );
      if (!property) {
        throw new Error(
          `Unable to find subquery property ${parameter.subquery_property_id}.`
        );
      }
      switch (property.type) {
        case 'int':
          return { type: 'int', property };
        case 'currency':
        case 'decimal':
          return { type: 'decimal', property };
        case 'date':
          return { type: 'datetime', property };
        case 'bit':
          return { type: 'boolean', property };
        case 'varchar100':
        case 'varcharmax':
          return { type: 'string', property };
        default:
          return { type: null, property };
      }
    }
    default:
      throw new Error(
        `Unexpected aggregate result type "${aggregateResultType}" for subquery property ${parameter.subquery_property_id}.`
      );
  }
}

export function mergeParameterValues(
  selected_parameter_values: PostReportArgs['parameter_values'],
  parameter_output_values: Record<string, string | number | null>
): ReportParameterValues {
  return {
    ...(selected_parameter_values ?? {}),
    ...Object.entries(parameter_output_values).reduce<ReportParameterValues>(
      (acc, [key, value]) => {
        if (value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {}
    )
  };
}

export function inheritParameterValues(
  parent: {
    parameters: ReportParameter[];
    parameter_types: Record<string, ExpressionReturnType>;
    parameter_values: ReportParameterValues;
  },
  parameters: ReportParameter[]
): ReportParameterValues {
  const { parameter_types, parameter_values } = parent;
  const inherited: ReportParameterValues = {};
  for (const parameter of parameters) {
    const { name, type } = parameter;
    if (
      type === 'expression' ||
      type === 'subquery' ||
      type === 'report-subquery'
    ) {
      continue;
    }
    if (
      type === 'activity' ||
      type === 'organization' ||
      type === 'project' ||
      type === 'user' ||
      type === 'choice'
    ) {
      const match = parent.parameters.find(p => {
        if (p.name !== name || p.type !== type) {
          return false;
        }
        if (p.type === 'choice') {
          return (
            parameter.type === 'choice' &&
            p.property_id === parameter.property_id
          );
        }
        if (p.type === 'activity') {
          return (
            parameter.type === 'activity' &&
            p.activity_definition_id === parameter.activity_definition_id
          );
        }
        return true;
      }) as
        | ActivityReportParameter
        | OrganizationReportParameter
        | ProjectReportParameter
        | UserReportParameter
        | ChoiceReportParameter
        | undefined;
      if (match) {
        inherited[name] = parameter_values[name] ?? match.value;
      }
      continue;
    }
    if (
      (type === 'literal-date' ||
        type === 'month-date' ||
        type === 'select-date') &&
      ['date', 'datetime'].includes(parameter_types[name])
    ) {
      inherited[name] = parameter_values[name];
      continue;
    }
    if (
      (type === 'literal-number' || type === 'select-number') &&
      ['int', 'decimal'].includes(parameter_types[name])
    ) {
      inherited[name] = parameter_values[name];
      continue;
    }
  }
  return inherited;
}

export function applyReportParametersToURLSearchParams(
  search: URLSearchParams,
  parameter_values: ReportParameterValues
) {
  for (const [key, value] of Object.entries(parameter_values)) {
    const name = `parameter_${key.substr(1).toLowerCase()}`;
    if (typeof value === 'string') {
      search.set(name, value);
    } else if (typeof value === 'number') {
      search.set(name, value.toString());
    } else if (Array.isArray(value)) {
      search.set(name, JSON.stringify(value));
    }
  }
  return search;
}

export function readReportParametersFromSearchParams(
  search: URLSearchParams,
  parameters: ReportParameter[]
): {
  parameter_values: ReportParameterValues;
  count: number;
} {
  const parameter_values: ReportParameterValues = {};
  const prefix = 'parameter_';
  let count = 0;
  for (let [key, value] of search) {
    value = value.trim();
    if (!key.toLowerCase().startsWith(prefix) || value === '') {
      continue;
    }
    const name = '$' + key.substr(prefix.length).toUpperCase();
    const parameter = parameters.find(p => p.name === name);
    switch (parameter?.type) {
      case 'literal-date':
      case 'month-date':
      case 'select-date': {
        if (validate(value, { type: 'string', format: 'date' })) {
          parameter_values[name] = value;
          count++;
        }
        continue;
      }
      case 'literal-number':
      case 'select-number': {
        const number = parseFloat(value);
        if (isFinite(number)) {
          parameter_values[name] = number;
          count++;
        }
        continue;
      }
      case 'activity':
      case 'user':
      case 'organization':
      case 'project': {
        let raw: LookupItem[];
        try {
          raw = JSON.parse(value);
        } catch {
          continue;
        }
        if (
          validate(raw, {
            type: 'array',
            minItems: 1,
            maxItems: reportLimits.inItems,
            items: uuid_lookup_item_schema
          }).valid
        ) {
          parameter_values[name] = raw;
          count++;
        }
        continue;
      }
      case 'choice': {
        let raw: LookupItem[];
        try {
          raw = JSON.parse(value);
        } catch {
          continue;
        }
        if (
          validate(raw, {
            type: 'array',
            minItems: 1,
            maxItems: reportLimits.inItems,
            items: integer_lookup_item_schema
          }).valid
        ) {
          parameter_values[name] = raw;
          count++;
        }
        continue;
      }
    }
  }
  return { parameter_values, count };
}

export const legacyChoiceReferencesToResolveInGrid = [
  wellKnownProperties.person.primary_phone.type,
  wellKnownProperties.activity.primary_phone.type,
  wellKnownProperties.person.primary_email.type,
  wellKnownProperties.activity.assignee_organization_primary_email.type,
  wellKnownProperties.activity.create_organization_primary_email.type,
  wellKnownProperties.user_organization.primary_phone.type,
  wellKnownProperties.organization_project.primary_phone.type
];

export function optimizeMinMaxDateSubqueries({
  filters,
  columns,
  parameters
}: ReportDefinition) {
  const map: Record<
    number,
    Record<
      'min' | 'max',
      `${'first' | 'last'}-${'started' | 'ended' | 'created'}`
    >
  > = {
    [wellKnownProperties.activity.start_date]: {
      min: 'first-started',
      max: 'last-started'
    },
    [wellKnownProperties.activity.end_date]: {
      min: 'first-ended',
      max: 'last-ended'
    },
    [wellKnownProperties.activity.create_date]: {
      min: 'first-created',
      max: 'last-created'
    }
  };

  const rewrite = (subquery: ReportFilter | ReportColumn | ReportParameter) => {
    if (
      (subquery.type !== 'subquery' && subquery.type !== 'report-subquery') ||
      (subquery.aggregate !== 'min' && subquery.aggregate !== 'max') ||
      ![
        wellKnownProperties.activity.start_date,
        wellKnownProperties.activity.end_date,
        wellKnownProperties.activity.create_date
      ].includes(subquery.subquery_property_id)
    ) {
      return;
    }
    const { aggregate, subquery_property_id } = subquery;
    subquery.aggregate = map[subquery_property_id][aggregate];
    if ('filters' in subquery) {
      for (const item of subquery.filters ?? []) {
        rewrite(item);
      }
    }
  };

  for (const item of [...filters, ...columns, ...parameters]) {
    rewrite(item);
  }
}
